import {
  PosUiActionResult,
  PurchaseClient,
  PurchaseOrderDetailsInput,
  TicketGroupInput,
} from 'src/WebApiController/index';

export interface BatchPurchaseProgress {
  /** Total number of distinct events */
  totalEvents: number;
  /** Number of events processed so far */
  processedEvents: number;
  /** Total number of ticket groups */
  totalTicketGroups: number;
  /** Number of ticket groups processed so far */
  processedTicketGroups: number;
  /** Current phase of the operation */
  phase: 'insert' | 'add';
}

export type BatchPurchaseProgressCallback = (
  progress: BatchPurchaseProgress
) => void;

const MAX_TICKET_GROUPS_PER_BATCH = 20;
const MAX_PARALLEL_REQUESTS = 4;

const groupTicketGroupsByEvent = (
  ticketGroups: TicketGroupInput[]
): Map<string, TicketGroupInput[]> => {
  const grouped = new Map<string, TicketGroupInput[]>();
  for (const tg of ticketGroups) {
    const key = tg.viagogoVirtualId ?? '';
    const existing = grouped.get(key);
    if (existing) {
      existing.push(tg);
    } else {
      grouped.set(key, [tg]);
    }
  }
  return grouped;
};

/**
 * Batches event keys so each batch contains up to MAX_TICKET_GROUPS_PER_BATCH
 * ticket groups. Events are never split across batches — if adding the next
 * event would exceed the limit, a new batch is started. An event is always
 * added to the current batch if it's empty (even if it alone exceeds the limit).
 */
const batchEventKeys = (
  eventKeys: string[],
  groupedByEvent: Map<string, TicketGroupInput[]>
): string[][] => {
  const batches: string[][] = [];
  let currentBatch: string[] = [];
  let currentCount = 0;

  for (const key of eventKeys) {
    const eventTGCount = groupedByEvent.get(key)!.length;

    if (
      currentBatch.length > 0 &&
      currentCount + eventTGCount > MAX_TICKET_GROUPS_PER_BATCH
    ) {
      batches.push(currentBatch);
      currentBatch = [];
      currentCount = 0;
    }

    currentBatch.push(key);
    currentCount += eventTGCount;
  }

  if (currentBatch.length > 0) {
    batches.push(currentBatch);
  }

  return batches;
};

const buildInput = (
  base: PurchaseOrderDetailsInput,
  ticketGroups: TicketGroupInput[],
  purchaseOrderId?: number
): PurchaseOrderDetailsInput => {
  return {
    ...base,
    id: purchaseOrderId ?? base.id,
    ticketGroups,
  };
};

/**
 * Creates a purchase order in batches to avoid performance issues when there
 * are too many ticket groups across many events.
 *
 * 1. Groups ticket groups by `viagogoVirtualId` (event).
 * 2. Batches events so each batch has up to 30 ticket groups (events are never split).
 * 3. Calls `insertPurchaseOrder` with the first batch.
 * 4. Iterates over remaining batches calling `addTicketGroupsToPurchaseOrder`.
 * 5. Reports progress via an optional callback after each batch completes.
 */
export class BatchedPurchaseOrderCreator {
  private onProgress: BatchPurchaseProgressCallback | null = null;
  private cancelled = false;
  private activePurchaseOrderId: number | null = null;

  constructor(
    private client: PurchaseClient,
    private enabled?: boolean
  ) {}

  /**
   * Aborts the in-progress batch creation and cancels the partially-created
   * purchase order on the server. Safe to call when no creation is in progress.
   */
  async abort(): Promise<void> {
    this.cancelled = true;
    const poId = this.activePurchaseOrderId;
    if (this.enabled && poId != null) {
      await this.client.cancelPurchaseOrder(poId, false, undefined);
      this.activePurchaseOrderId = null;
    }
  }

  subscribe(callback: BatchPurchaseProgressCallback): () => void {
    this.onProgress = callback;
    return () => {
      if (this.onProgress === callback) {
        this.onProgress = null;
      }
    };
  }

  async create(
    input: PurchaseOrderDetailsInput
  ): Promise<PosUiActionResult | null> {
    this.cancelled = false;
    this.activePurchaseOrderId = null;

    if (!this.enabled) {
      return this.client.insertPurchaseOrder(input, true);
    }

    const groupedByEvent = groupTicketGroupsByEvent(input.ticketGroups);
    const eventKeys = Array.from(groupedByEvent.keys());

    const totalEvents = eventKeys.length;
    const totalTicketGroups = input.ticketGroups.length;

    let processedEvents = 0;
    let processedTicketGroups = 0;

    const emitProgress = (phase: BatchPurchaseProgress['phase']) => {
      this.onProgress?.({
        totalEvents,
        processedEvents,
        totalTicketGroups,
        processedTicketGroups,
        phase,
      });
    };

    // Split event keys into batches of up to MAX_TICKET_GROUPS_PER_BATCH TGs
    const batches = batchEventKeys(eventKeys, groupedByEvent);

    // First batch: insertPurchaseOrder
    const firstBatchKeys = batches[0];
    const firstBatchTGs = firstBatchKeys.flatMap(
      (key) => groupedByEvent.get(key)!
    );

    emitProgress('insert');

    const insertResult = await this.client.insertPurchaseOrder(
      buildInput(input, firstBatchTGs),
      true
    );

    processedEvents += firstBatchKeys.length;
    processedTicketGroups += firstBatchTGs.length;
    emitProgress('insert');

    const purchaseOrderId = insertResult.entityId;
    if (purchaseOrderId == null) {
      return insertResult;
    }

    this.activePurchaseOrderId = purchaseOrderId;

    if (this.cancelled) {
      return insertResult;
    }

    // Remaining batches: addTicketGroupsToPurchaseOrder with concurrency queue
    // Reserve the last batch to run alone so PO-level aggregation is correct
    const remainingBatches = batches.slice(1);
    const parallelBatches = remainingBatches.slice(0, -1);
    const lastBatch = remainingBatches[remainingBatches.length - 1];

    const onBatchDone = (eventCount: number, tgCount: number) => {
      processedEvents += eventCount;
      processedTicketGroups += tgCount;
      emitProgress('add');
    };

    if (parallelBatches.length > 0) {
      const failedResult = await this.runAddQueue(
        parallelBatches,
        groupedByEvent,
        input,
        purchaseOrderId,
        onBatchDone
      );
      if (failedResult) {
        await this.client.cancelPurchaseOrder(
          purchaseOrderId,
          false,
          undefined
        );
        this.activePurchaseOrderId = null;
        return failedResult;
      }
    }

    // Run the last batch alone to ensure PO-level aggregation is calculated correctly
    if (lastBatch && !this.cancelled) {
      const lastBatchTGs = lastBatch.flatMap((key) => groupedByEvent.get(key)!);
      const lastResult = await this.client.addTicketGroupsToPurchaseOrder(
        buildInput(input, lastBatchTGs, purchaseOrderId),
        true
      );
      if (lastResult.entityId == null) {
        await this.client.cancelPurchaseOrder(
          purchaseOrderId,
          false,
          undefined
        );
        this.activePurchaseOrderId = null;
        return lastResult;
      }
      onBatchDone(lastBatch.length, lastBatchTGs.length);
    }

    this.activePurchaseOrderId = null;
    return insertResult;
  }

  /**
   * Processes add-ticket-group batches with at most MAX_PARALLEL_REQUESTS
   * in flight at a time. Returns the first failed result, or null if all succeeded.
   */
  private async runAddQueue(
    batches: string[][],
    groupedByEvent: Map<string, TicketGroupInput[]>,
    input: PurchaseOrderDetailsInput,
    purchaseOrderId: number,
    onBatchDone: (eventCount: number, tgCount: number) => void
  ): Promise<PosUiActionResult | null> {
    let nextIndex = 0;
    let failedResult: PosUiActionResult | null = null;

    const processNext = async (): Promise<void> => {
      while (nextIndex < batches.length && !failedResult && !this.cancelled) {
        const index = nextIndex++;
        const batchKeys = batches[index];
        const batchTGs = batchKeys.flatMap((key) => groupedByEvent.get(key)!);

        const addResult = await this.client.addTicketGroupsToPurchaseOrder(
          buildInput(input, batchTGs, purchaseOrderId),
          true
        );

        if (addResult.entityId == null) {
          failedResult = addResult;
          return;
        }

        onBatchDone(batchKeys.length, batchTGs.length);
      }
    };

    const workers = Array.from(
      { length: Math.min(MAX_PARALLEL_REQUESTS, batches.length) },
      () => processNext()
    );
    await Promise.all(workers);

    return failedResult;
  }
}
